import { Box, CircularProgress, DialogContent, Divider, Grid, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Avatar from '@hapana/shared/web/components/shared/avatar/avatar.component'
import {
  AVATAR_SIZE,
  AVATAR_TYPES,
} from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import BirthdayAlert from '@hapana/shared/web/components/shared/individual-alerts/birthday-alert/birthday-alert.component'
import FirstSessionAlert from '@hapana/shared/web/components/shared/individual-alerts/first-session-alert/first-session-alert.component'
import FormsPendingIndividualAlert from '@hapana/shared/web/components/shared/individual-alerts/forms-pending-individual-alert/forms-pending-individual-alert.component'
import IntroOffersAlert from '@hapana/shared/web/components/shared/individual-alerts/intro-offers-alert/intro-offers-alert.component'
import LocationAlert from '@hapana/shared/web/components/shared/individual-alerts/location-alert/location-alert.component'
import PackagesIndividualAlert from '@hapana/shared/web/components/shared/individual-alerts/packages-individual-alert/packages-individual-alert.component'
import PaymentsIndividualAlert from '@hapana/shared/web/components/shared/individual-alerts/payments-alert/payments-alert.component'
import Tabs from '@hapana/shared/web/components/shared/tabs/horizontal-tabs/tabs.component'
import {
  useClientActivePackages,
  useClientNotesAndProperties,
} from '@hapana/shared/web/hooks/client.hooks'
import { isBirthdayWithinWeek } from '@hapana/shared/utils/date.util'

import { styles } from './modal-alerts.styles'
import { TABS_VALUES } from './notes-properties/modal-alerts.constants'
import { NOTE_ALERT_TYPES } from './notes-properties/notes-properties.constants'
import NotesType from './notes-properties/notes-type.component'

const ModalAlerts = ({
  siteId,
  profileImg,
  name,
  firstName,
  failedPayments,
  formsPending,
  homeLocations,
  birthday,
  isFirstSession,
  introOffer,
  suspendedClient,
  clientId,
  tabDefaultValue,
  hasNotesAndProperties,
  location,
}) => {
  const { t } = useTranslation(['checkIn'])
  const { data, isLoading: isNotesLoading } = useClientNotesAndProperties(clientId)
  const { data: packagesData, isLoading: isPackagesLoading } = useClientActivePackages(clientId)
  const isHomeLocation = !homeLocations || homeLocations.length === 0
  const showBirthday = birthday && isBirthdayWithinWeek(birthday)
  const hasGeneralAlerts =
    failedPayments?.length > 0 ||
    packagesData?.clientSiteActivePackages?.length > 0 ||
    formsPending?.length > 0 ||
    (homeLocations ?? false) ||
    showBirthday ||
    isFirstSession ||
    (introOffer ?? false) ||
    (suspendedClient ?? false)

  const totalAlertsCount =
    (failedPayments?.length || 0) +
    (packagesData?.clientSiteActivePackages?.length > 0 || 0) +
    (formsPending?.length > 0 || 0) +
    (data?.clientSiteNotes.length || 0) +
    (data?.clientSiteAlertedProperties.length || 0) +
    ((introOffer && 1) || 0) +
    ((isFirstSession && 1) || 0) +
    ((suspendedClient && 1) || 0) +
    ((showBirthday && 1) || 0) +
    (homeLocations?.length > 0 || 0)

  const showTabs = () => (
    <Tabs
      tabs={[
        {
          label: t('checkIn:general'),
          value: TABS_VALUES[0],
          content: (
            <DialogContent sx={styles.generalAlertsWrapperInTabs}>
              {generalAlertsContent()}
            </DialogContent>
          ),
        },
        {
          label: t('checkIn:notesAndProperties'),
          value: TABS_VALUES[1],
          content: (
            <DialogContent sx={styles.generalAlertsWrapperInTabs}>
              {notesAndPropertiesContent()}
            </DialogContent>
          ),
        },
      ]}
      defaultValue={tabDefaultValue}
    />
  )

  const generalAlertsContent = () => {
    return (
      <Box sx={styles.selectsSection}>
        {failedPayments?.length > 0 && (
          <>
            <PaymentsIndividualAlert
              failedPayments={failedPayments}
              siteId={siteId}
              isHomeLocation={isHomeLocation}
            />
            <Divider sx={styles.divider} />
          </>
        )}
        {formsPending?.length > 0 && (
          <>
            <FormsPendingIndividualAlert formsPending={formsPending} />
            <Divider sx={styles.divider} />
          </>
        )}
        {homeLocations?.length > 0 && (
          <>
            <LocationAlert homeLocations={homeLocations} />
            <Divider sx={styles.divider} />
          </>
        )}
        {showBirthday && (
          <>
            <BirthdayAlert birthday={birthday} />
            <Divider sx={styles.divider} />
          </>
        )}
        {packagesData?.clientSiteActivePackages?.length > 0 && (
          <>
            <PackagesIndividualAlert packages={packagesData.clientSiteActivePackages} />
            <Divider sx={styles.divider} />
          </>
        )}
        {isFirstSession && (
          <>
            <FirstSessionAlert firstName={firstName} />
            <Divider sx={styles.divider} />
          </>
        )}
        {introOffer && (
          <>
            <IntroOffersAlert location={location} />
            <Divider sx={styles.divider} />
          </>
        )}
      </Box>
    )
  }

  const notesAndPropertiesContent = () => (
    <>
      <Box sx={styles.notesTypeWrapper}>
        <NotesType type={NOTE_ALERT_TYPES.NOTE} alerts={data?.clientSiteNotes} username={name} />
      </Box>
      <Divider />
      <Box sx={styles.notesTypeWrapper}>
        <NotesType
          type={NOTE_ALERT_TYPES.PROPERTIES}
          alerts={data?.clientSiteAlertedProperties}
          username={name}
        />
      </Box>
    </>
  )

  const displayContent = () => {
    if (hasGeneralAlerts && hasNotesAndProperties) {
      return showTabs()
    }
    if (hasGeneralAlerts && !hasNotesAndProperties) {
      return (
        <DialogContent sx={styles.generalAlertsWrapper}>{generalAlertsContent()}</DialogContent>
      )
    }
    if (hasNotesAndProperties && !hasGeneralAlerts) {
      return (
        <DialogContent sx={styles.generalAlertsWrapper}>
          {notesAndPropertiesContent()}
        </DialogContent>
      )
    }
  }

  return (
    <DialogLayout
      closeType={CLOSE_TYPES.ICON}
      size={SIZES.SM}
      hasBackLabel
      headerStylesOverride={styles.dialogHeader}
    >
      {isPackagesLoading || isNotesLoading ? (
        <Box sx={styles.loading}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Divider sx={styles.divider} />
          <DialogContent sx={styles.titleSection}>
            <Typography marginBottom={1} typography="header2">
              {t('checkIn:totalAlerts', {
                count: totalAlertsCount,
              })}
            </Typography>
            <Grid container alignItems="center">
              <Avatar
                item={{ src: profileImg?.url, alt: `${name}'s avatar` }}
                avatarType={AVATAR_TYPES.SINGLE_AVATAR}
                size={AVATAR_SIZE.EXTRA_SMALL}
              />
              <Typography marginLeft={1} color="gray.64" typography="header4">
                {name}
              </Typography>
            </Grid>
          </DialogContent>
          <DialogContent sx={styles.contentWrapper}>{displayContent()}</DialogContent>
        </>
      )}
    </DialogLayout>
  )
}

ModalAlerts.propTypes = {
  profileImg: PropTypes.shape({
    url: PropTypes.string,
  }),
  name: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  failedPayments: PropTypes.array,
  packages: PropTypes.array,
  formsPending: PropTypes.array,
  homeLocations: PropTypes.arrayOf(PropTypes.string),
  birthday: PropTypes.bool,
  isFirstSession: PropTypes.bool,
  introOffer: PropTypes.string,
  suspendedClient: PropTypes.string,
  siteId: PropTypes.string,
  hasNotesAndProperties: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      siteId: PropTypes.string,
    }),
  ]),
  clientId: PropTypes.string,
  tabDefaultValue: PropTypes.string,
  totalAlertsCount: PropTypes.number,
  location: PropTypes.string,
}

ModalAlerts.defaultProps = {
  tabDefaultValue: TABS_VALUES[0],
}

export default ModalAlerts
